import { Grid } from '@mui/material';
import { useEffect } from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { useLoadOptions } from "src/hooks/useLoadOptions";
import { HttpCacheOperacion } from 'src/pages/sistema/api/httpCacheOperacion';
import { AsyncSelect, ControlledDatePicker, ControlledTextField } from "../../../../../components/forms";
import CardActions from '../../../components/CardActions';
import { SectionBox } from '../../../components/FieldView';
import { useLCTCSectorComercialTipos, useLCTCUnidadNegocioTipos } from '../hooks/useLCTCCache';
import { OperacionLCTCCuentaFields, OperacionLCTCFields, OperacionLCTCRelacionFields, OperacionLCTCSectorComercialFields, OperacionLCTCUnidadNegocioFields } from '../types/fields';
import { FormOperacionLCTC, FormOperacionLCTCCuenta } from "../types/types";
import { SectorComercialFields } from 'src/types';

interface SeccionNuevoLCTCDatosOperacionProps {
    formMethods: UseFormReturn<FormOperacionLCTC>
    codSistemaPrincipal: number,
    codSistema: number
}

export const SeccionNuevoLCTCDatosOperacion = ({
    formMethods,
    codSistemaPrincipal,
    codSistema
}: SeccionNuevoLCTCDatosOperacionProps) => {
    const { control, setValue, resetField } = formMethods
    const unidadNegocioTipos = useLCTCUnidadNegocioTipos(codSistemaPrincipal, codSistema);
    const loadUnidadNegocio = useLoadOptions({
        data: unidadNegocioTipos.unidadNegocioTipos,
        loading: unidadNegocioTipos.isLoading,
        isError: unidadNegocioTipos.isError,
        error: unidadNegocioTipos.error
    });

    const watchedUnidadNegocio = useWatch({ control: control, name: OperacionLCTCFields.CodUnidadNegocio });

    const watchedSectorComercial = useWatch({ control: control, name: OperacionLCTCFields.CodSectorComercial });

    const sectorComercialTipos = useLCTCSectorComercialTipos(watchedUnidadNegocio, codSistema);
    const loadSectorComercial = useLoadOptions({
        data: sectorComercialTipos.sectorComercialTipos,
        loading: sectorComercialTipos.isLoading,
        isError: sectorComercialTipos.isError,
        error: sectorComercialTipos.error
    });

    useEffect(() => {
        // Cuando cambia la unidad de negocio, reseteamos el sector comercial y las cuentas.
        resetField(OperacionLCTCFields.CodSectorComercial);
        setValue(OperacionLCTCFields.Cuentas, []);
    }, [watchedUnidadNegocio, setValue]);

    useEffect(() => {
        if (!watchedSectorComercial) {
            setValue(OperacionLCTCFields.Cuentas, []);

            return;
        }

        const generarCuentas = async () => {
            const relaciones = await HttpCacheOperacion.getLCTCRelacionTipos(watchedSectorComercial);
            const nuevasCuentas: FormOperacionLCTCCuenta[] = relaciones.map(relacion => ({
                [OperacionLCTCCuentaFields.CodClienteTipo]: relacion[OperacionLCTCRelacionFields.CodClienteTipo],
                [OperacionLCTCCuentaFields.DescClienteTipo]: relacion[OperacionLCTCRelacionFields.DescClienteTipo],
                [OperacionLCTCCuentaFields.CodClienteSubTipo]: relacion[OperacionLCTCRelacionFields.CodClienteSubTipo],
                [OperacionLCTCCuentaFields.DescClienteSubTipo]: relacion[OperacionLCTCRelacionFields.DescClienteSubTipo],
                [OperacionLCTCCuentaFields.LimiteConcertacion]: '',
                [OperacionLCTCCuentaFields.LimiteLiquidacionDVP]: '',
                [OperacionLCTCCuentaFields.LimiteLiquidacionFOP]: '',
            }));

            setValue(OperacionLCTCFields.Cuentas, nuevasCuentas);
        };

        generarCuentas();
    }, [watchedSectorComercial, setValue]);

    return (
        <CardActions title='Datos Operación' icon='mdi:briefcase-outline' colapsable>
            <SectionBox>
                <Grid item xs={12} md={4}>
                    <AsyncSelect
                        name={OperacionLCTCFields.CodUnidadNegocio}
                        loadOptions={loadUnidadNegocio}
                        valueField={SectorComercialFields.UnidadNegocioCodigo}
                        control={control}
                        label='Unidad de negocio'
                        selectNotNulleable
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <AsyncSelect
                        name={OperacionLCTCFields.CodSectorComercial}
                        loadOptions={loadSectorComercial}
                        valueField={SectorComercialFields.SectorComercialCodigo}
                        control={control}
                        label='Equipo Comercial'
                        selectNotNulleable
                        disabled={!watchedUnidadNegocio}
                        fullWidth
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ControlledDatePicker
                        name={OperacionLCTCFields.FechaSolicitud}
                        control={control}
                        label='Fecha Solicitud'
                        disabled
                    />
                </Grid>
                <Grid item xs={12} md={4}>
                    <ControlledDatePicker
                        name={OperacionLCTCFields.FechaVencimiento}
                        control={control}
                        label='Fecha Vencimiento'
                    />
                </Grid>
                <Grid item xs={12}>
                    <ControlledTextField
                        control={control}
                        name={OperacionLCTCFields.Observaciones}
                        label='Observaciones'
                        multiline={true}
                        minRows={3}
                        fullWidth
                    />
                </Grid>
            </SectionBox>
        </CardActions>
    )
}
